import dedent from 'ts-dedent'
import {
  ColumnDataType,
  DOSourceDefinition,
  DOSourceVariable,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentInput,
  WorkflowComponentMetadata,
  WorkflowComponentParamType,
  WorkflowEnvVars,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType
} from '../../types'
import { DataWarehouseConnection } from '../../workflow/connections'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './singleoutputcomponent'

export function extractDOSourceLocation(doSourceQuery: string): string {
  const tableMatch = doSourceQuery.match(/\bFROM\s+([^\s+]+)/im)
  if (!tableMatch) {
    throw new Error(`Could not find subscription table in DO query: "${doSourceQuery}"`)
  }
  const subTableName = tableMatch[1]
  // HOTFIX: remove quotes from doSource
  // implemented here waiting for fix into AT
  // ref: https://app.shortcut.com/cartoteam/story/348155/dataobs-enrich-in-snowflake-cannot-handled-quoted-source
  let cleanedTableName = subTableName.replace(/`/g, '').split('.').slice(0, -1).join('.')
  cleanedTableName = cleanedTableName.replace(/"/g, '')
  return cleanedTableName
}

export function splitPairs(tokens: Array<string>): Array<Array<string>> {
  const pairs: Array<Array<string>> = []
  if (tokens) {
    for (let index = 0; index < tokens.length; index += 2) {
      pairs.push([tokens[index], tokens[index + 1]])
    }
  }
  return pairs
}

export function hasDuplicateItems(items: string): boolean {
  const itemsAggrs = splitPairs(items?.split(',')?.map((x) => x?.trim())).map((item) => item.join(','))
  return [...new Set(itemsAggrs)].length < itemsAggrs.length
}

export function itemsToSQL(itemLabel: string, items: string, connection: DataWarehouseConnection): string {
  const itemsAggrs = splitPairs(items?.split(',')?.map((x) => x?.trim()))
  switch (connection.type) {
    case Provider.BigQuery:
      // [('population_93405ad7', 'sum')],
      return `[${itemsAggrs.map(([name, aggr]) => `('${name}', '${aggr}')`).join(', ')}]`
    case Provider.Snowflake:
      // ARRAY_CONSTRUCT(OBJECT_CONSTRUCT('variable', 'population_93405ad7', 'aggregation', 'sum'))
      return `ARRAY_CONSTRUCT(${itemsAggrs
        .map(([name, aggr]) => `OBJECT_CONSTRUCT('${itemLabel}', '${name}', 'aggregation', '${aggr}')`)
        .join(', ')})`
    case Provider.Redshift:
      // '[["population_93405ad7", "avg"]]'
      return `'${JSON.stringify(itemsAggrs)}'`
    default:
      return ''
  }
}

export function variablesToSQL(variables: string, connection: DataWarehouseConnection): string {
  return itemsToSQL('variable', variables, connection)
}

export function columnsToSQL(variables: string, connection: DataWarehouseConnection): string {
  return itemsToSQL('column', variables, connection)
}

function variableAggregations(variables: string) {
  const varAggrs = splitPairs(variables?.split(',')?.map((x) => x?.trim()))
  return varAggrs.map(([name, aggr]) => `${aggr}(${name}) AS ${name}_${aggr}`).join(', ')
}

function variableColumns(variables: string) {
  return uniqueVariableNames(variables).join(', ')
}

function dotype_to_dbtype(dotype: string | undefined, provider: Provider): string | undefined {
  switch (provider) {
    case Provider.BigQuery:
      switch (dotype) {
        case 'FLOAT':
          return 'FLOAT64'
      }
  }
  return dotype
}

function variableColumnDefinitions(
  variables: string,
  variableDefinitions: DOSourceVariable[],
  connection: DataWarehouseConnection
) {
  const varNames = uniqueVariableNames(variables)
  return varNames
    .map((name) => {
      const dbType = dotype_to_dbtype(variableDefinitions.find((v) => v.name === name)?.dbType, connection.type)
      if (!dbType) {
        throw new Error(`Variable ${name} not found in DO source`)
      }
      return `${connection.castValue('NULL', dbType)} AS ${name}`
    })
    .join(', ')
}

function uniqueVariableNames(variables: string) {
  const varAggrs = splitPairs(variables?.split(',')?.map((x) => x?.trim()))
  const varNames = varAggrs.map(([name]) => name)
  return [...new Set(varNames)]
}

export function outputToSQL(varName: string, connection: DataWarehouseConnection): string {
  switch (connection.type) {
    case Provider.BigQuery:
      return `[${toSQLVar(varName, connection)}]`
    case Provider.Snowflake:
      return `TO_ARRAY(${toSQLVar(varName, connection)})`
    case Provider.Redshift:
      return toSQLVar(varName, connection)
    default:
      return ''
  }
}

export function toSQLVar(varName: string, connection: DataWarehouseConnection): string {
  switch (connection.type) {
    case Provider.BigQuery:
      return `${varName}`
    case Provider.Snowflake:
      return `:${varName}`
    case Provider.Redshift:
      return `${varName}`
    default:
      return ''
  }
}

function beginBlock(connection) {
  switch (connection.type) {
    case Provider.BigQuery:
      return dedent`
      BEGIN
      DECLARE tempInputTable STRING DEFAULT '${connection.tempLocation}.' || REPLACE(GENERATE_UUID(), '-', '_');
      DECLARE tempOutputTable STRING DEFAULT '${connection.tempLocation}.' || REPLACE(GENERATE_UUID(), '-', '_');
      DECLARE tempOutputTableKring STRING DEFAULT '${connection.tempLocation}.' || REPLACE(GENERATE_UUID(), '-', '_');
      DECLARE tempOutputTableKringRenamed STRING DEFAULT '${connection.tempLocation}.' || REPLACE(GENERATE_UUID(), '-', '_');`
    case Provider.Snowflake:
      return dedent`
      DECLARE
        tempInputTable STRING DEFAULT '${connection.quoteName(
          connection.tempLocation
        )}.outputtable' || REPLACE(UUID_STRING(), '-', '_');
        tempOutputTable STRING DEFAULT '${connection.quoteName(
          connection.tempLocation
        )}.outputtable' || REPLACE(UUID_STRING(), '-', '_');
      BEGIN
      `
    case Provider.Redshift:
      return dedent`
      DECLARE
        tempInputTable VARCHAR(MAX) DEFAULT '${connection.quoteName(
          connection.tempLocation
        )}.outputtable' || REPLACE(EXTRACT(EPOCH FROM CURRENT_TIME)::VARCHAR, '.', '');
        tempOutputTable VARCHAR(MAX) DEFAULT '${connection.quoteName(
          connection.tempLocation
        )}.outputtable' || REPLACE(EXTRACT(EPOCH FROM CURRENT_TIME)::VARCHAR, '.', '');
      BEGIN`
    default:
      return ''
  }
}

function endBlock(connection) {
  return 'END;'
}

/**
 * EnrichComponent is the base class for all components that enrich a table with
 * data from another table or DO table.
 */
export abstract class EnrichComponent extends SingleOutputWorkflowComponent {
  /**
   * The name of the command to be used for the enrichment. If source is a DO table,
   * then the command will prepend with DATAOBS_
   */
  commandName: string | null = null
  gridType: string | null = null

  /**
   * @param metadata The component metadata
   * @param commandName The name of the command to be used for the enrichment. If source is a DO table,
   * then the command will prepend with DATAOBS_
   */
  constructor(metadata: WorkflowComponentMetadata, commandName: string, gridType: string | null = null) {
    const inputs: Array<WorkflowComponentInput> = [
      {
        name: 'target',
        type: WorkflowComponentParamType.InputTable,
        title: 'Target to be enriched',
        placeholder: 'FQN of the target table'
      },
      {
        name: 'source',
        type: WorkflowComponentParamType.InputTable,
        title: 'Source table',
        description: 'Source for the enrichment',
        placeholder: 'FQN of the source table'
      },
      {
        name: 'target_col',
        type: WorkflowComponentParamType.Column,
        title: 'Target geo column',
        parent: 'target',
        dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
      },
      {
        // Only used if workflow.tableDOSource(params.source, connection) === null
        name: 'source_col',
        type: WorkflowComponentParamType.Column,
        title: 'Source geo column',
        parent: 'source',
        dataType: [ColumnDataType.Geography, ColumnDataType.Geometry],
        optional: false
      },
      {
        name: 'variables',
        type: WorkflowComponentParamType.SelectColumnAggregation,
        title: 'Variables',
        parent: 'source',
        noDefault: true,
        doVariables: true,
        description:
          'Comma-separated list of variable names and aggregation methods (e.g. population_93405ad7, sum, population_93405ad7, avg)',
        placeholder: 'population_93405ad7, sum, population_93405ad7, avg',
        optional: false
      }
    ]
    if (gridType) {
      inputs.push({
        name: 'kring',
        type: WorkflowComponentParamType.Number,
        title: 'Kring size',
        min: 0,
        default: 0,
        providers: [Provider.BigQuery]
      })
      inputs.push({
        name: 'decay',
        type: WorkflowComponentParamType.Selection,
        title: 'Decay function',
        options: ['uniform', 'inverse', 'inverse_square', 'exponential'],
        default: 'uniform',
        providers: [Provider.BigQuery]
      })
    }
    super(metadata, inputs)

    if (gridType) {
      this.gridType = gridType
    }
    this.commandName = commandName
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const preparedParams = this._prepareParameters(params)
    const doSource = workflow.tableDOSource(preparedParams.source as string, connection)
    const tableName = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)

    const variables = preparedParams.variables as string
    const targetTable = connection.quoteName(preparedParams.target as string)
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)

    let completeQuery: string
    if (dryRun) {
      // We'll build a query like this one to obtain the result columns with the proper types:
      //   SELECT * FROM
      //     (SELECT * FROM source LIMIT 0) AS __target_columns,
      //     (SELECT agr1(col1) AS col1_agr1, ... FROM
      //       (SELECT ... LIMIT 0) AS __source_columns
      //     ) AS __source_aggregations
      // Note that we must actually apply the aggregation functions because the resulting type depends
      // on both the underlying column and the aggregation function (e.g. the AVG of an int may be a float).
      completeQuery = connection.getCreateTempTableQuery(
        tableName,
        this._buildDryRunQuery(preparedParams, doSource, connection),
        WorkflowCodeType.Full,
        null
      )
    } else {
      let createTableQuery = connection.getCreateTempTableQuery(
        '##tablename##',
        `SELECT * FROM ${targetTable}`,
        WorkflowCodeType.Full,
        null
      )

      const createTempInputTable = connection.executeDynamicSQL({
        sql: createTableQuery,
        addQuotes: true,
        replacements: [["'##tablename##'", toSQLVar('tempInputTable', connection)]]
      })

      const callQuery = this._buildCallQuery(preparedParams, doSource, connection, envVars)

      let createFinalTable = ''
      let kring = ''
      if (preparedParams.kring) {
        let kringInputTable = 'tempOutputTableKring'
        let renaming = ''
        if (preparedParams.target_col !== 'index') {
          createTableQuery = connection.getCreateTempTableQuery(
            '##tablename##',
            `SELECT
              *,
              ${preparedParams.target_col} AS index
            FROM ${connection.quoteName('##tablename2##')}`,
            WorkflowCodeType.Full,
            null
          )
          renaming = connection.executeDynamicSQL({
            sql: createTableQuery,
            addQuotes: true,
            replacements: [
              ["'##tablename##'", toSQLVar('tempOutputTableKringRenamed', connection)],
              ["'##tablename2##'", toSQLVar('tempOutputTableKring', connection)]
            ]
          })
          kringInputTable = 'tempOutputTableKringRenamed'
        }
        kring = dedent`
          ${renaming}
          CALL ${at}.__KRING_DECAY(
            '${this.gridType}',
            CAST(${preparedParams.kring} AS INTEGER),
            '${preparedParams.decay}',
            ${variablesToSQL(variables, connection)},
            ${toSQLVar(kringInputTable, connection)},
            ${toSQLVar(kringInputTable, connection)},
            ${toSQLVar('tempOutputTable', connection)}
          );
          `
        createTableQuery = connection.getCreateTempTableQuery(
          tableName,
          dedent`
            SELECT b.*, a.* EXCEPT(index)
            FROM
              ${connection.quoteName('##tablename##')} a,
              ${connection.quoteName('##tablename2##')} b
            WHERE a.index = b.${preparedParams.target_col}
            `,
          codeType,
          null
        )
        createFinalTable = connection.executeDynamicSQL({
          sql: createTableQuery,
          addQuotes: true,
          replacements: [
            ["'##tablename##'", toSQLVar('tempOutputTable', connection)],
            ["'##tablename2##'", toSQLVar('tempInputTable', connection)]
          ]
        })
      } else {
        createTableQuery = connection.getCreateTempTableQuery(
          tableName,
          `SELECT * FROM ${connection.quoteName('##tablename##')}`,
          codeType,
          null
        )
        createFinalTable = connection.executeDynamicSQL({
          sql: createTableQuery,
          addQuotes: true,
          replacements: [["'##tablename##'", toSQLVar('tempOutputTable', connection)]]
        })
      }

      completeQuery = dedent`
        ${beginBlock(connection)}
        ${createTempInputTable}
        ${callQuery}
        ${kring}
        ${createFinalTable}
        ${endBlock(connection)}
      `
    }

    completeQuery = connection.wrapCodeInIfTableExistsBlock(completeQuery, [tableName], codeType)

    return {
      outputs: { result: tableName },
      query: completeQuery
    }
  }

  protected _buildDryRunQuery(
    preparedParams: Record<string, unknown>,
    doSource: DOSourceDefinition | null,
    connection: DataWarehouseConnection
  ): string {
    const variables = preparedParams.variables as string
    const targetTable = connection.quoteName(preparedParams.target as string)
    const sourceTable = connection.quoteName(preparedParams.source as string)

    const targetColumnsSubquery = `SELECT * FROM ${targetTable} LIMIT 0`

    let sourceColumnsSubquery: string
    if (doSource) {
      // We need the doSource.variables with their dbTypes because
      // the variable names are identifiers (slugs) from which the column names cannot be derived.
      if (!doSource.variables) {
        throw new Error('DO source variables not found')
      }

      const sourceColumns = variableColumnDefinitions(variables, doSource.variables, connection)
      sourceColumnsSubquery = `SELECT ${sourceColumns} LIMIT 0`
    } else {
      sourceColumnsSubquery = dedent`
        SELECT ${variableColumns(variables)}
        FROM ${sourceTable}
        LIMIT 0
      `
    }

    const sourceAggregations = variableAggregations(variables)
    const sourceAggregationsSubquery = dedent`
       SELECT
        ${sourceAggregations}
       FROM (
        ${sourceColumnsSubquery}
       ) AS __source_columns
    `
    return dedent`
      SELECT * FROM
        (
          ${targetColumnsSubquery}
        ) AS __target_columns,
        (
          ${sourceAggregationsSubquery}
        ) AS __source_aggregations
    `
  }

  protected _buildCallQuery(
    preparedParams: Record<string, unknown>,
    doSource: DOSourceDefinition | null,
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars
  ): string {
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    const variables = preparedParams.variables as string
    const sourceTable = connection.quoteName(preparedParams.source as string)

    let query: string

    if (doSource) {
      const doSourceLocation = extractDOSourceLocation(doSource.query)

      query = dedent`
        CALL ${at}.DATAOBS_${this.commandName}(
          ${this.gridType ? `'${this.gridType}',` : ''}
          ${toSQLVar('tempInputTable', connection)},
          '${preparedParams.target_col}',
          ${variablesToSQL(variables, connection)},
          NULL,
          ${outputToSQL(preparedParams.kring ? 'tempOutputTableKring' : 'tempOutputTable', connection)},
          '${doSourceLocation}'
        );
      `
    } else {
      query = dedent`
        CALL ${at}.${this.commandName}(
          ${this.gridType ? `'${this.gridType}',` : ''}
          ${toSQLVar('tempInputTable', connection)},
          '${preparedParams.target_col}',
          'SELECT * FROM ${sourceTable}',
          '${preparedParams.source_col}',
          ${columnsToSQL(variables, connection)},
          ${outputToSQL(preparedParams.kring ? 'tempOutputTableKring' : 'tempOutputTable', connection)}
        );
      `
    }

    query = query.replace(/^\s*[\r\n]/gm, '') // remove empty lines from the query
    return query
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams): WorkflowParameterValueError[] {
    const errors = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    const preparedParams = this._prepareParameters(resolvedParamValues)
    if (preparedParams.source === preparedParams.target) {
      errors.push({
        parameter: 'source',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'Source and target tables cannot be the same'
      })
    }
    if (preparedParams.variables == null) {
      errors.push({
        parameter: 'variables',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'Variables must be provided'
      })
    } else if ((preparedParams.variables as string).split(',').length < 2) {
      errors.push({
        parameter: 'variables',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'At least a variable and an aggregation method must be provided'
      })
    } else if (hasDuplicateItems(preparedParams.variables as string)) {
      errors.push({
        parameter: 'variables',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'Variable-aggregation combinations must be unique'
      })
    }
    return errors
  }

  protected _checkInputValueIsNotMissing(
    params: WorkflowComponentCheckInputValuesParams,
    input: WorkflowComponentInput,
    errors: Array<WorkflowParameterValueError>
  ) {
    // The source_col input is not optional when the source is not a DO source,
    // but it isn't required or used with DO sources.
    // We cannot fully validate this case since we don't know at this point
    // if params.source is a DO source or not, so we'll regard it optional for the
    // validation. Note that we cannot just mark it optional 'cause the UI would show
    // it as optional.
    if (input.name !== 'source_col') {
      super._checkInputValueIsNotMissing(params, input, errors)
    }
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, Array<string>> = {}
    classifiedMethods[ColumnDataType.Number] = ['min', 'max', 'avg', 'sum', 'count']
    return {
      methods: classifiedMethods,
      allowedTypes: [ColumnDataType.Number],
      excludedNames: ['geoid', 'geom']
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }
}
